import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronLeft, Gauge, Calendar, Fuel, Cog, Zap, Palette, DoorOpen,
  Users, Leaf, Check, Phone, MapPin,
} from "lucide-react";
import { cars, brand as company, formatPrice, formatKm } from "../mock";
import CarCard from "../components/CarCard";
import ContactForm from "../components/ContactForm";
import { Button } from "../components/ui/button";

export default function CarDetail() {
  const { id } = useParams();
  const car = cars.find((c) => c.id === id);
  const [active, setActive] = useState(0);

  if (!car) {
    return (
      <main className="pt-32 pb-20 text-center min-h-screen">
        <p className="text-gray-500">Auto non trovata.</p>
        <Link to="/auto" className="text-[#e2001a] font-semibold mt-4 inline-block">Torna al catalogo</Link>
      </main>
    );
  }

  const specs = [
    { icon: Gauge, label: "Chilometri", value: formatKm(car.km) },
    { icon: Calendar, label: "Immatricolazione", value: `${car.month}/${car.year}` },
    { icon: Fuel, label: "Alimentazione", value: car.fuel },
    { icon: Cog, label: "Cambio", value: car.gearbox },
    { icon: Zap, label: "Potenza", value: car.power },
    { icon: Palette, label: "Colore", value: car.color },
    { icon: DoorOpen, label: "Porte", value: car.doors },
    { icon: Users, label: "Posti", value: car.seats },
    { icon: Leaf, label: "Emissioni", value: car.emission },
  ];

  const similar = cars.filter((c) => c.id !== car.id && c.body === car.body).slice(0, 4);

  return (
    <main className="pt-24 pb-20 bg-[#f5f6f8] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Link to="/auto" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#e2001a] mb-6">
          <ChevronLeft className="w-4 h-4" /> Torna al catalogo
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Gallery */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl overflow-hidden bg-white border border-gray-100">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={car.images[active]} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-3">
              {car.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`aspect-[4/3] rounded-xl overflow-hidden border-2 transition-colors ${
                    active === i ? "border-[#e2001a]" : "border-transparent"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#16181d] text-white text-xs font-semibold px-3 py-1 rounded-full">{car.condition}</span>
                {car.neopatentati && (
                  <span className="bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full">Neopatentati</span>
                )}
              </div>
              <h1 className="font-display font-extrabold text-2xl text-[#16181d] leading-tight">
                {car.brand} {car.model}
              </h1>
              <p className="text-gray-500 mt-1">{car.trim}</p>

              <div className="mt-5 pb-5 border-b border-gray-100">
                <p className="text-xs text-gray-400">Prezzo</p>
                <p className="font-display font-extrabold text-4xl text-[#e2001a]">{formatPrice(car.price)}</p>
                <p className="text-xs text-gray-400 mt-1">IVA non deducibile</p>
              </div>

              <div className="mt-5 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 text-[#e2001a]" /> Sede di {car.location}
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <a href={company.phoneHref}>
                  <Button className="w-full h-12 rounded-xl bg-[#e2001a] hover:bg-[#c00016] font-semibold">
                    <Phone className="w-4 h-4 mr-2" /> Chiama ora
                  </Button>
                </a>
                <a href="#contatti">
                  <Button variant="outline" className="w-full h-12 rounded-xl mt-3 font-semibold">
                    Richiedi informazioni
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-10">
          <div className="lg:col-span-3 space-y-8">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-display font-bold text-xl mb-5">Scheda tecnica</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                {specs.map((s) => (
                  <div key={s.label} className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-lg bg-[#f5f6f8] flex items-center justify-center shrink-0">
                      <s.icon className="w-4 h-4 text-[#e2001a]" />
                    </span>
                    <div>
                      <p className="text-xs text-gray-400">{s.label}</p>
                      <p className="font-semibold text-[#16181d] text-sm">{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-display font-bold text-xl mb-3">Descrizione</h2>
              <p className="text-gray-600 leading-relaxed">{car.description}</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-display font-bold text-xl mb-5">Dotazioni e optional</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {car.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" /> {f}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-display font-bold text-xl mb-4">Consumi ed emissioni</h2>
              <ul className="space-y-3 text-sm">
                <li className="flex justify-between"><span className="text-gray-500">Consumo combinato</span><span className="font-semibold">{car.consumption}</span></li>
                <li className="flex justify-between"><span className="text-gray-500">Emissioni CO2</span><span className="font-semibold">{car.co2}</span></li>
                <li className="flex justify-between"><span className="text-gray-500">Classe emissione</span><span className="font-semibold">{car.emission}</span></li>
              </ul>
              <p className="text-[11px] text-gray-400 mt-4 leading-relaxed">
                Valori indicativi secondo procedura WLTP. Verifica sempre le caratteristiche
                del veicolo insieme al concessionario prima dell'acquisto.
              </p>
            </div>
          </div>
        </div>

        {/* Similar */}
        {similar.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display font-extrabold text-2xl text-[#16181d] mb-6">Auto simili</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {similar.map((c) => <CarCard key={c.id} car={c} />)}
            </div>
          </div>
        )}
      </div>

      <div className="mt-14">
        <ContactForm prefill={`Sono interessato a: ${car.brand} ${car.model} ${car.trim} (${formatPrice(car.price)})`} />
      </div>
    </main>
  );
}
